import { useRef, useEffect, useLayoutEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Sheet from './Sheet'
import ActionBar from './ActionBar'
import Grid from '@mui/material/Grid'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'

/**
 * Whether a Scan button should be offered at all.
 *
 * Feature detection rather than user-agent sniffing, because iPads report
 * themselves as desktop Safari and would be wrongly excluded. A device with no
 * camera still passes this and fails later with NotFoundError, which the
 * viewport reports cleanly - that is a better trade than an async
 * enumerateDevices() call on every render.
 */
export function isCameraAvailable() {
    return Boolean(window.isSecureContext && navigator.mediaDevices?.getUserMedia)
}

// Catalog keys, not copy: this map is module-level and cannot call a hook, so
// `error` state carries the key and the Alert translates it at render. That
// also means a language switch while the viewport is open re-translates the
// message instead of freezing whichever language it was raised in.
const ERRORS = {
    NotAllowedError: 'scanner.cameraBlocked',
    NotFoundError: 'scanner.noCamera',
    NotReadableError: 'scanner.cameraInUse',
}

// Our tags carry 6-8 numeric digits. Identical to the rule the scanner-gun
// keydown buffer in InventoryCheck.js already enforces.
const VALID_BARCODE = /^\d{6,8}$/

// A stationary tag decodes on nearly every frame; without this a single tag
// would register ten times a second.
const DUPLICATE_WINDOW_MS = 1500

// Cropping does NOT magnify: drawImage with equal source and destination rects
// copies 1:1, so the bars land on identical pixels either way. What it changes
// is the barcode's SHARE of the width of the image handed to ZXing, and that -
// measured, not assumed - is what decides a 1D read.
//
// Sweep against a real store tag (barcode 24662770, 230px wide in the photo):
//
//     crop 320 wide -> share 72% -> reads      crop 500 -> share 46% -> no read
//     crop 400 wide -> share 57% -> reads      crop 768 -> share 30% -> no read
//     crop 460 wide -> share 50% -> no read
//
// Height is nearly irrelevant by comparison: 460x150 (width share 50%, height
// share 53%) fails, while 320x300 (width share 72%, height share 27%) reads.
//
// The trap is that the required crop width depends on how close the operator is
// holding the phone. Up close a barcode might span 800px and need a ~1200px
// window; at arm's length it spans 240px and needs ~400px. No single fixed crop
// serves both - a wide one starves a distant tag, and a narrow one slices a
// close one in half, which is unreadable no matter how sharp it is. Both of
// those shipped, in that order.
//
// So we cycle scales across successive ticks instead. Each scale S covers
// barcodes spanning S/1.82 to S of the frame, so spacing them a factor of ~0.6
// apart (1.67x) leaves the coverage bands overlapping with no gap. Together
// these four span barcodes from ~12% to 100% of frame width, and at 10fps every
// scale still gets ~2.5 attempts a second.
const CROP_SCALES = [1.0, 0.6, 0.36, 0.21]

// Tag-shaped. The barcode is far wider than it is tall, so a squarer window just
// dilutes it with background and thumb.
const CROP_ASPECT = 0.3

// What the reticle marks. Not a decode region - the decode regions cycle - but
// the framing that lands a barcode inside the most useful of them.
const RETICLE_WIDTH = 0.8

// ZXing is lazy-loaded on first tap, on a phone that may be on spotty store
// wifi - a failed chunk fetch is expected, not exceptional.
const DECODER_LOAD_ERROR = 'scanner.loadFailed'

export default function BarcodeScanner(props) {
    const {
        open,
        onClose,
        onScan,
        continuous = false,
        paused = false,
        title,
        feedback = null,
        children
    } = props

    const { t } = useTranslation()

    // Resolved after the hook, not as a parameter default: defaults are
    // evaluated in the signature, before useTranslation() has run.
    const titleText = title ?? t('scanner.defaultTitle')

    const videoRef = useRef(null)
    const streamRef = useRef(null)
    const canvasRef = useRef(null)
    // Counts frames so the decode region can cycle through CROP_SCALES.
    const tickRef = useRef(0)
    const [reticle, setReticle] = useState(null)
    const decoderRef = useRef(null)
    const lastScanRef = useRef({ value: null, at: 0 })
    const audioRef = useRef(null)
    const [error, setError] = useState(null)
    const [toast, setToast] = useState(null)
    const [flash, setFlash] = useState(false)
    const [restartToken, setRestartToken] = useState(0)

    // The host composes the message (only it knows what a barcode resolved to);
    // the scanner owns how long it stays up. A new feedback value replaces
    // whatever is still showing rather than queuing behind it.
    useEffect(() => {
        if (!feedback) return
        setToast(feedback)
        const timer = setTimeout(() => setToast(null), 2500)
        return () => clearTimeout(timer)
    }, [feedback])

    // The decode loop reads these through refs rather than through its closure, and
    // they are deliberately NOT in the effect's dependency array.
    //
    // Two reasons, both load-bearing. Putting `paused` in the deps would tear the
    // decoder down and dynamically re-import ZXing every time it toggles - and
    // InventoryCheck toggles it on EVERY scanned item, so that is a full library
    // re-import per item. Putting `onScan` in the deps is no better: PrintOtherTags
    // recreates that handler on every render because it closes over `barcodes`, so
    // the loop would be rebuilt constantly. Refs give the loop the current value
    // without making its identity depend on it.
    const liveRef = useRef({ paused, continuous, onScan, onClose })
    // useLayoutEffect, not useEffect: a passive effect flushes in a scheduler
    // task after paint, and the decode interval is also a task, ticking every
    // 100ms - so a tick can in principle land between "commit where paused
    // became true" and "passive effect that would have updated liveRef",
    // observing a stale paused: false. This is the feature's hardest
    // correctness constraint (InventoryCheck's per-item verification pause),
    // and the sync itself does no DOM work, so a layout effect closes that
    // window entirely at no cost.
    useLayoutEffect(() => {
        liveRef.current = { paused, continuous, onScan, onClose }
    })

    // A plain `ref={videoRef}` only assigns srcObject at the call site inside
    // startCamera, which fails whenever the <video> remounts AFTER that
    // assignment ran - e.g. recovering from an error: setError(null) is
    // batched under React 18 and not yet flushed when startCamera reads
    // videoRef.current, so it is still null (the Alert is showing instead of
    // the video). A callback ref instead fires every time the element itself
    // mounts, so it can (re)attach whatever stream is already sitting in
    // streamRef.current - which by then is always set, since it is written
    // synchronously before setError(null) above it.
    const attachVideoRef = useCallback(node => {
        videoRef.current = node
        if (node && streamRef.current) node.srcObject = streamRef.current
    }, [])

    function releaseCamera() {
        streamRef.current?.getTracks().forEach(track => track.stop())
        streamRef.current = null
    }

    /**
     * A short confirmation tone. This is the only feedback a clerk who is looking
     * at the tag rather than the screen will register - iOS Safari implements no
     * navigator.vibrate, so sound is all we have.
     *
     * Does not construct the AudioContext itself - the layout effect below
     * creates it on open, inside the tap that opens the scanner, which is the
     * user gesture iOS requires to unlock audio at all. By the time a scan
     * happens (the earliest is ~100ms later, inside a setInterval tick, well
     * outside that gesture), the context already exists; this just uses it.
     */
    function beep() {
        try {
            const context = audioRef.current
            if (!context) return
            // iOS also suspends an open AudioContext (like it does the camera
            // track) when Safari backgrounds or the phone locks, even though it
            // is never closed - see the layout effect below. resume() here
            // recovers that; it works even outside a gesture because this
            // context was already unlocked once, at creation.
            if (context.state === 'suspended') context.resume()
            const oscillator = context.createOscillator()
            const gain = context.createGain()
            oscillator.frequency.value = 1200
            gain.gain.value = 0.1
            oscillator.connect(gain)
            gain.connect(context.destination)
            oscillator.start()
            oscillator.stop(context.currentTime + 0.08)
        } catch (error) {
            // Audio is confirmation, not function. A browser that refuses it must
            // not take the scan down with it.
        }
    }

    useEffect(() => {
        if (!open) {
            releaseCamera()
            return
        }

        let cancelled = false

        async function startCamera() {
            try {
                // Safari negotiates down from whatever we ask for. Asking high is
                // the only zoom available to us - there is no zoom constraint on
                // iOS - so resolution plus a center crop is the entire strategy
                // for reading a 0.2mm bar width.
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        facingMode: { ideal: 'environment' },
                        width: { ideal: 3840 },
                        height: { ideal: 2160 },
                    },
                })

                if (cancelled) return stream.getTracks().forEach(track => track.stop())

                streamRef.current = stream
                setError(null)
                if (videoRef.current) videoRef.current.srcObject = stream
            } catch (err) {
                if (!cancelled) setError(ERRORS[err.name] || 'scanner.startFailed')
            }
        }

        startCamera()

        return () => {
            cancelled = true
            releaseCamera()
        }
    }, [open, restartToken])

    // iOS suspends the camera track when Safari backgrounds or the phone locks,
    // leaving a frozen frame that looks live but decodes nothing. Bumping
    // restartToken re-runs the camera effect above to re-acquire the stream.
    useEffect(() => {
        if (!open) return

        function handleVisibilityChange() {
            if (document.visibilityState === 'visible') setRestartToken(token => token + 1)
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
    }, [open])

    // Creates the AudioContext on open, not lazily on first scan: BarcodeScanner
    // is conditionally rendered by every consumer
    // ({showScanner && <BarcodeScanner open ... />}), so mount IS the tap that
    // grants iOS's user-gesture allowance for audio. The first scan happens
    // ~100ms later inside a setInterval tick, well outside that window - a
    // context built there instead would come up permanently suspended, with
    // nothing for beep()'s resume() to recover.
    //
    // useLayoutEffect rather than useEffect: layout effects flush synchronously
    // during the same commit that the tap's state update triggers, before the
    // browser paints - as close as React gets to staying inside the click
    // event's own call stack. useEffect is scheduled as a separate, deferred
    // task and risks running after WebKit considers the gesture's activation
    // window to have expired.
    //
    // Deliberately keyed on [open] alone, NOT [open, restartToken] like the
    // camera effect above. That effect's cleanup re-runs on every restartToken
    // bump (every return-to-visible), and tearing the AudioContext down there
    // used to kill the beep for the rest of the session after a single
    // lock-screen or backgrounding cycle: the next scan would lazily rebuild a
    // *replacement* context from inside the decode tick, which iOS leaves
    // permanently suspended because it was never built inside a user gesture.
    // This context instead survives background/foreground cycles and is torn
    // down only when the scanner actually closes or unmounts; beep() resumes it
    // if iOS suspended it (without closing it) in the background.
    useLayoutEffect(() => {
        if (!open) return

        try {
            audioRef.current = new AudioContext()
            if (audioRef.current.state === 'suspended') audioRef.current.resume()
        } catch (error) {
            // Same contract as beep(): audio is confirmation, not function. A
            // browser that refuses to construct it must not block scanning.
        }

        return () => {
            // Known limitation, not a bug to chase: per the Web Audio spec, close()
            // discards any still-scheduled output. In non-continuous mode (e.g.
            // SelectBarcode), beep()'s oscillator.stop(currentTime + 0.08) has not
            // finished when onScan() -> onClose() unmounts this component in the
            // same flush, so this close() cuts the 80ms tone off within a few ms -
            // effectively silent on the one-shot edit flow, even though the exact
            // same beep() call is audible on the two continuous screens (where the
            // scanner stays open and this cleanup does not run right after the
            // scan). Harm is low: the screen changes immediately, so there is
            // still visual confirmation. BarcodeScanner.test.js:333's
            // ("beeps on an accepted scan") jsdom AudioContext stub has no notion
            // of close() silencing scheduled output, so it cannot catch this - if
            // an on-device check turns up "no beep on the edit flow," this is why,
            // not a new regression. Restructuring the close path to let the tone
            // finish is deliberately out of scope here.
            audioRef.current?.close()
            audioRef.current = null
        }
    }, [open])

    useEffect(() => {
        if (!open || error) return

        let cancelled = false
        let decoderPromise = null

        // Memoised so every tick shares one in-flight decoder-creation promise
        // instead of racing its own dynamic import(). A tick that fires before
        // the decoder is ready awaits this rather than bailing out - bailing
        // would silently drop that frame instead of decoding it once ready.
        function getDecoder() {
            if (!decoderPromise) {
                decoderPromise = (async () => {
                    const { createDecoder } = await import('../barcodeDecoder')
                    const decoder = await createDecoder()
                    if (cancelled) {
                        decoder.destroy()
                        return null
                    }
                    decoderRef.current = decoder
                    return decoder
                })()
            }
            return decoderPromise
        }

        // Kick off decoder creation immediately on mount rather than waiting
        // for the first tick - the cleanup below destroys decoderRef.current
        // as soon as it exists, and a scan popup opened and closed before a
        // single 100ms tick fires should still release ZXing's resources.
        //
        // If it fails, report through the same `error` state Task 3 uses for
        // camera failures. That also tears this effect down, since `error` is
        // a dependency below - without it, every future tick would await this
        // same permanently-rejected promise forever, an unhandled rejection
        // every 100ms for as long as the popup stays open. Recovery is
        // close-and-reopen, same as a camera error.
        getDecoder().catch(() => {
            if (!cancelled) setError(DECODER_LOAD_ERROR)
        })

        const interval = setInterval(async () => {
            // Captured synchronously, before the decoder-ready await below, so
            // the dedup window is measured against the frame this tick actually
            // fired on rather than whenever its async chain happens to settle.
            const now = Date.now()
            if (liveRef.current.paused || !videoRef.current || !canvasRef.current) return

            let decoder
            try {
                decoder = await getDecoder()
            } catch {
                // Already reported by the catch above; nothing more to do here.
                return
            }
            if (cancelled || !decoder) return

            const video = videoRef.current
            const canvas = canvasRef.current

            tickRef.current += 1

            // One scale per tick, round-robin, so each frame costs a single
            // decode however many scales we cover.
            const scale = CROP_SCALES[tickRef.current % CROP_SCALES.length]
            const cropWidth = Math.min(video.videoWidth, video.videoWidth * scale)
            const cropHeight = Math.min(video.videoHeight, cropWidth * CROP_ASPECT)

            // Aiming guide, sized in element space rather than from a decode
            // region - those cycle, so tracking one would make the box twitch.
            // Only set state when the size actually changes: this runs ten times
            // a second, and re-rendering the whole viewport each time to write an
            // identical value is pure waste.
            const elW = video.clientWidth
            const elH = video.clientHeight
            if (elW && elH) {
                const w = Math.round(elW * RETICLE_WIDTH)
                const h = Math.min(Math.round(elH * 0.8), Math.round(w * CROP_ASPECT * 2.2))
                setReticle(current => (current && current.w === w && current.h === h ? current : { w, h }))
            }

            if (!cropWidth || !cropHeight) return

            canvas.width = cropWidth
            canvas.height = cropHeight
            canvas.getContext('2d').drawImage(
                video,
                (video.videoWidth - cropWidth) / 2,
                (video.videoHeight - cropHeight) / 2,
                cropWidth,
                cropHeight,
                0, 0, cropWidth, cropHeight
            )

            const result = await decoder.decodeFromCanvas(canvas)

            if (cancelled || !result || !VALID_BARCODE.test(result)) return

            const last = lastScanRef.current
            if (last.value === result && now - last.at < DUPLICATE_WINDOW_MS) return
            lastScanRef.current = { value: result, at: now }

            beep()
            setFlash(true)
            setTimeout(() => setFlash(false), 150)
            liveRef.current.onScan(result)
            if (!liveRef.current.continuous) liveRef.current.onClose()
        }, 100)

        return () => {
            cancelled = true
            clearInterval(interval)
            decoderRef.current?.destroy()
            decoderRef.current = null
        }
        // paused/continuous/onScan/onClose are intentionally absent - see liveRef above.
        // Adding them re-imports ZXing on every pause toggle, which InventoryCheck does
        // once per scanned item.
    }, [open, error])

    if (!open) return null

    return (
        // fullScreen, because a live camera view is the screen rather than a layer
        // over it -- the sheet's 85svh cap would leave the page showing behind the
        // video. Close lives in the sheet's own footer now, so it stays put while
        // the viewport, the toast and any caller's children scroll past it.
        <Sheet
            open={open}
            onClose={onClose}
            title={titleText}
            fullScreen
            actions={
                /* Closing the camera destroys nothing, so it stops being red. */
                <ActionBar cancel={{ label: t('common.close'), onClick: onClose }} />
            }
        >
            <Grid container direction='column' gap={2} alignItems='center'>
                {error ? (
                    <Grid item>
                        <Alert severity='error'>{t(error)}</Alert>
                    </Grid>
                ) : (
                    <Grid item width='100%' sx={{ position: 'relative', lineHeight: 0 }}>
                        {/* The viewport owns the size; the video only fills it.
                            The <video> must NOT size itself, because a getUserMedia
                            video has no intrinsic dimensions when it mounts -
                            videoWidth/videoHeight are 0 until loadedmetadata fires,
                            several frames after this element is first laid out. A
                            height of `auto` is therefore resolved against the CSS
                            default object size of 300x150, a 2:1 box, and
                            `object-fit: cover` renders a hard horizontal centre-crop
                            of the camera frame into it. That is the cut-off strip
                            clerks report on first open, and it persists on any phone
                            whose browser does not re-run layout when the real
                            intrinsic size finally lands - rotating to landscape and
                            back is simply the cheapest way to force the full layout
                            invalidation that repairs it.
                            A definite height here removes the dependency outright:
                            the box is correct on the first paint, before the camera
                            has reported anything, so there is nothing left to
                            correct later. Same pattern as the image boxes in
                            FindPieceResults and PhotoUpload.
                            Plain inline styles rather than `sx` so the height stays
                            on the element itself, where a test can read it. */}
                        <div
                            data-testid='scanner-viewport'
                            style={{
                                position: 'relative',
                                width: '100%',
                                height: paused ? '64px' : '50vh',
                                overflow: 'hidden',
                                borderRadius: '12px',
                                outline: flash ? '4px solid #2e7d32' : '4px solid transparent',
                                transition: 'outline-color 120ms, height 120ms',
                            }}
                        >
                            <video
                                ref={attachVideoRef}
                                autoPlay
                                muted
                                playsInline
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    display: 'block',
                                }}
                            />

                            {/* Corner marks on the region actually handed to the decoder.
                                Sized from the same crop maths the tick uses, so it cannot
                                drift away from what is really being read. Inside the
                                viewport, so it centres on the video rather than on
                                whatever else shares the grid cell. */}
                            {!paused && reticle && (
                                <Box
                                    aria-hidden
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        width: `${reticle.w}px`,
                                        height: `${reticle.h}px`,
                                        maxWidth: '100%',
                                        pointerEvents: 'none',
                                        '&::before, &::after, & > span::before, & > span::after': {
                                            content: '""',
                                            position: 'absolute',
                                            width: '22px',
                                            height: '22px',
                                            border: '3px solid rgba(255,255,255,0.95)',
                                        },
                                        '&::before': { top: 0, left: 0, borderRight: 'none', borderBottom: 'none' },
                                        '&::after': { top: 0, right: 0, borderLeft: 'none', borderBottom: 'none' },
                                        '& > span::before': { bottom: 0, left: 0, borderRight: 'none', borderTop: 'none' },
                                        '& > span::after': { bottom: 0, right: 0, borderLeft: 'none', borderTop: 'none' },
                                    }}
                                >
                                    <span />
                                </Box>
                            )}
                        </div>

                        <canvas ref={canvasRef} style={{ display: 'none' }} />
                    </Grid>
                )}

                {toast && (
                    <Grid item width='100%'>
                        <Alert severity={toast.type}>{toast.message}</Alert>
                    </Grid>
                )}

                {children}
            </Grid>
        </Sheet>
    )
}
